import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import NotificationRow from "@/components/notifications/NotificationRow";
import { TYPE_META } from "@/components/notifications/notificationMeta";
import { useMarkRead, useNotificationFeed } from "@/components/notifications/useNotifications";

const TABS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  ...Object.entries(TYPE_META).map(([key, m]) => ({ key, label: m.label })),
];

const matches = (tab) => (i) => tab === "all" || (tab === "unread" ? !i.read : i.type === tab);

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const { data: items = [], isLoading } = useNotificationFeed({ queryKey: ["notifications-all"], limit: 50 });
  const { markRead, markAllRead } = useMarkRead();

  const visible = items.filter(matches(tab));
  const unread = items.filter((i) => !i.read).length;

  const openItem = (item) => {
    markRead(item);
    navigate(item.actionUrl);
  };

  return (
    <div data-testid="notifications-page" className="max-w-3xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl sm:text-[2.6rem] leading-tight text-foreground">Notifications</h1>
          <p className="text-muted-foreground mt-1">
            {unread ? `${unread} unread — approvals, AI previews and follow-ups that need you.` : "You're all caught up."}
          </p>
        </div>
        {unread > 0 && (
          <button data-testid="notifications-mark-all-read" onClick={markAllRead} className="self-start sm:self-auto text-sm font-medium text-accent hover:underline">
            Mark all as read
          </button>
        )}
      </div>

      <section className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex gap-2 overflow-x-auto p-3 border-b border-border" role="tablist">
          {TABS.map((t) => {
            const active = tab === t.key;
            const count = items.filter(matches(t.key)).length;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                data-testid={`notifications-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={cn(
                  "shrink-0 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition-colors",
                  active ? "bg-primary border-primary text-primary-foreground" : "bg-card border-border text-foreground hover:bg-secondary",
                )}
              >
                {t.label}
                <span className={cn("rounded-md px-1.5 text-xs tabular-nums", active ? "bg-primary-foreground/20" : "bg-secondary text-muted-foreground")}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {!isLoading && visible.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <span className="mx-auto mb-3 h-12 w-12 rounded-full bg-success/10 text-success flex items-center justify-center">
              <CheckCheck className="h-5 w-5" />
            </span>
            <p className="text-sm text-muted-foreground">{tab === "unread" ? "No unread notifications." : "Nothing here right now."}</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {visible.map((item, idx) => (
              <li key={item.key || item.id}>
                <NotificationRow item={item} onOpen={openItem} size="lg" testId={`notifications-item-${idx}`} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
